import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Package, RefreshCw, Wrench, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import { toast } from "sonner";

/**
 * Phase 101dq (Feb 2026) — Panneau diagnostic d'un slot MOMA-calc.
 *
 * Ouvert via clic sur "🔍" dans MomaReassortWidget. Expose :
 *  - Baseline (tour + qty + date)
 *  - Chronologie mouvements depuis la baseline
 *  - Ventes Nayax depuis la baseline
 *  - Lots AM actuels au slot
 *  - Stock Nayax LIVE via /machineProducts (source de vérité machine)
 *  - Bouton "Aligner AM sur Nayax" (ou toute qty cible) qui crée/ajuste un lot
 */
export default function SlotAuditDrawer({ open, onOpenChange, distributor, slot, onAdjusted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [targetQty, setTargetQty] = useState("");
  const [dlcInput, setDlcInput] = useState("");
  const [lotNumberInput, setLotNumberInput] = useState("");

  const load = useCallback(async () => {
    if (!distributor?.id || !slot) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/distributors/${distributor.id}/slot-audit`,
        { params: { slot_number: slot.slot_number, product_id: slot.product_id } }
      );
      setData(data);
      // Pré-remplit target avec la qty Nayax si dispo — utilise la forme
      // fonctionnelle pour NE PAS dépendre de `targetQty` (sinon boucle infinie
      // avec l'useEffect qui reset targetQty à "" au montage).
      if (data.nayax_live_qty != null) {
        setTargetQty((prev) => (prev === "" ? String(data.nayax_live_qty) : prev));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement audit");
    } finally {
      setLoading(false);
    }
  }, [distributor, slot]);

  useEffect(() => {
    if (open) {
      setData(null);
      setTargetQty("");
      setDlcInput("");
      setLotNumberInput("");
      load();
    }
  }, [open, load]);

  const applyAdjust = async () => {
    const t = parseFloat(targetQty);
    if (Number.isNaN(t) || t < 0) {
      toast.error("Quantité cible invalide");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        slot_number: slot.slot_number,
        product_id: slot.product_id,
        target_qty: t,
      };
      if (dlcInput) payload.dlc = dlcInput;
      if (lotNumberInput) payload.lot_number = lotNumberInput;
      const { data: res } = await api.post(
        `/distributors/${distributor.id}/slot-audit/adjust`,
        payload
      );
      const action = res.action === "noop" ? "aucun changement" :
                     res.action === "created_lot" ? `+${res.delta} unité(s) créée(s)` :
                     `${res.delta} unité(s) retirée(s)`;
      toast.success(`AM aligné à ${t} · ${action}`);
      await load();
      // Phase 101dr — Notifie le parent pour rafraîchir la table
      if (typeof onAdjusted === "function") onAdjusted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur alignement");
    } finally {
      setBusy(false);
    }
  };

  const hasDlcAmbiguity = useMemo(() => {
    if (!data) return false;
    const dlcs = [...new Set((data.am_lots || []).map((l) => l.dlc).filter(Boolean))];
    return dlcs.length > 1;
  }, [data]);

  const targetInt = parseFloat(targetQty);
  const delta = data && !Number.isNaN(targetInt) ? targetInt - data.am_qty : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="slot-audit-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1F4E3D]" />
            Audit slot {slot?.slot_number}
            <Badge variant="outline" className="ml-2 font-mono">
              {distributor?.name}
            </Badge>
          </SheetTitle>
          <SheetDescription className="text-xs">
            {slot?.product_name}{slot?.product_sku ? ` · ${slot.product_sku}` : ""}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-12 text-center text-[#575A57] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement audit…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-4 mt-4 pb-8">
            {/* KPIs : 3 sources en 1 clin d'œil */}
            <div className="grid grid-cols-3 gap-2">
              <StatBox label="Nayax live" value={data.nayax_live_qty} error={data.nayax_error} tone="nayax" />
              <StatBox label="AM lots" value={data.am_qty} tone="am" />
              <StatBox label="MOMA-calc" value={slot?.qty_moma_calc} tone="moma" />
            </div>
            {data.nayax_error && (
              <div className="bg-[#FBEDEA] border border-[#C05C47] rounded-md p-2 text-[11px] text-[#8C3D31]">
                <div className="font-semibold mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Nayax indisponible :
                </div>
                <code className="text-[10px] break-all">{typeof data.nayax_error === "string" ? data.nayax_error : JSON.stringify(data.nayax_error)}</code>
              </div>
            )}

            {/* Baseline */}
            <div className="border border-[#E5E5E0] rounded-md p-3 bg-[#FAFAF7]">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1">Baseline (dernière tournée)</div>
              {data.baseline ? (
                <div className="text-sm">
                  <span className="font-mono font-semibold">{data.baseline.tour_reference}</span>
                  <span className="mx-2">·</span>
                  <span>{data.baseline.restock_qty} unité(s) chargée(s)</span>
                  <span className="mx-2">·</span>
                  <span className="text-[#6B6E6B]">le {new Date(data.baseline.restock_at).toLocaleString("fr-CH")}</span>
                  <div className="text-[10px] text-[#8C6B2F] mt-0.5">source : {data.baseline.source}</div>
                </div>
              ) : (
                <div className="text-sm italic text-[#8C6B2F]">
                  Aucune tournée n&apos;a chargé ce slot dans AM. Historique des 30 derniers jours affiché ci-dessous.
                </div>
              )}
            </div>

            {/* Ventes Nayax */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1">
                Ventes Nayax depuis baseline ({(data.sales || []).length})
              </div>
              {(data.sales || []).length === 0 ? (
                <div className="text-sm text-[#575A57] italic">Aucune vente enregistrée.</div>
              ) : (
                <div className="border border-[#E5E5E0] rounded-md divide-y">
                  {(data.sales || []).map((s) => (
                    <div key={s.id} className="p-2 text-xs flex justify-between">
                      <div>
                        <span className="font-mono">{s.external_id || s.id.slice(0, 8)}</span>
                        {s.lot_number && <span className="text-[#6B6E6B] ml-2">lot {s.lot_number}</span>}
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-semibold">{s.quantity}</div>
                        <div className="text-[10px] text-[#6B6E6B]">{new Date(s.sold_at).toLocaleString("fr-CH")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mouvements AM */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1">
                Mouvements AM depuis baseline ({(data.movements || []).length})
              </div>
              {(data.movements || []).length === 0 ? (
                <div className="text-sm text-[#575A57] italic">Aucun mouvement AM (Comptoir, retrait, ajustement).</div>
              ) : (
                <div className="border border-[#E5E5E0] rounded-md divide-y max-h-64 overflow-y-auto">
                  {(data.movements || []).map((m, idx) => (
                    <div key={idx} className="p-2 text-xs">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge className={
                            m.type === "IN" || (m.type === "TRANSFER" && m.to_distributor_id === distributor.id) ? "bg-[#1F4E3D]" :
                            m.type === "OUT" || m.type === "WASTE" ? "bg-[#C05C47]" :
                            "bg-[#8C6B2F]"
                          }>{m.type}</Badge>
                          <span className="font-mono ml-2 font-semibold">
                            {m.type === "IN" || (m.type === "TRANSFER" && m.to_distributor_id === distributor.id) ? "+" : "−"}
                            {Math.abs(m.quantity || 0)}
                          </span>
                        </div>
                        <div className="text-[10px] text-[#6B6E6B]">{new Date(m.created_at).toLocaleString("fr-CH")}</div>
                      </div>
                      {m.reason && (
                        <div className="text-[11px] text-[#575A57] mt-1">{m.reason}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lots AM actuels */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1">
                Lots AM actuels au slot ({(data.am_lots || []).length})
              </div>
              {(data.am_lots || []).length === 0 ? (
                <div className="text-sm text-[#575A57] italic">Aucun lot AM au slot.</div>
              ) : (
                <div className="border border-[#E5E5E0] rounded-md divide-y">
                  {(data.am_lots || []).map((l) => (
                    <div key={l.id} className="p-2 text-xs flex justify-between">
                      <div>
                        <span className="font-mono font-semibold">{l.lot_number}</span>
                        {l.dedicated_to_order_id && <Badge className="ml-2 bg-[#8C6B2F]">dédié</Badge>}
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-semibold">{l.quantity} u</div>
                        {l.dlc && <div className="text-[10px] text-[#6B6E6B]">DLC {l.dlc}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action : Aligner */}
            <div className="border-2 border-[#1F4E3D] rounded-md p-3 bg-[#EAF4EE]">
              <div className="text-sm font-semibold text-[#1F4E3D] mb-2 flex items-center gap-1">
                <Wrench className="w-4 h-4" /> Aligner AM sur une quantité cible
              </div>
              <div className="text-[11px] text-[#575A57] mb-2">
                Crée un lot si target &gt; AM · Décrémente le plus récent si target &lt; AM · No-op si égal.
                {data.nayax_live_qty != null && (
                  <span className="text-[#1F4E3D] font-medium"> Suggestion : Nayax live = {data.nayax_live_qty}.</span>
                )}
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <Label className="text-[10px] mb-1 block">Quantité cible</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={targetQty}
                    onChange={(e) => setTargetQty(e.target.value)}
                    className="w-24 h-9"
                    data-testid="slot-audit-target-qty"
                  />
                </div>
                {delta > 0 && (data.am_lots || []).length === 0 && (
                  <div>
                    <Label className="text-[10px] mb-1 block">N° de lot (ajout)</Label>
                    <Input
                      value={lotNumberInput}
                      onChange={(e) => setLotNumberInput(e.target.value)}
                      placeholder="Ex : 260818-BROCH"
                      className="w-44 h-9 font-mono"
                      data-testid="slot-audit-lot-number"
                    />
                  </div>
                )}
                {delta > 0 && (hasDlcAmbiguity || (data.am_lots || []).length === 0) && (
                  <div>
                    <Label className="text-[10px] mb-1 block">DLC pour le lot ajouté</Label>
                    <Input
                      type="date"
                      value={dlcInput}
                      onChange={(e) => setDlcInput(e.target.value)}
                      className="w-40 h-9"
                      data-testid="slot-audit-dlc"
                    />
                  </div>
                )}
                <Button
                  onClick={applyAdjust}
                  disabled={busy || targetQty === ""}
                  className="bg-[#1F4E3D] hover:bg-[#173B2E] h-9"
                  data-testid="slot-audit-apply"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Appliquer{delta !== 0 ? ` (Δ ${delta > 0 ? "+" : ""}${delta})` : ""}
                </Button>
                <Button variant="outline" size="sm" onClick={load} className="h-9" title="Rafraîchir les données Nayax + AM">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatBox({ label, value, tone, error }) {
  const styles = {
    nayax: { color: "#8C3D31", bg: "#FBEDEA", border: "#C05C47" },
    am:    { color: "#1F4E3D", bg: "#EAF4EE", border: "#5A8F76" },
    moma:  { color: "#8C6B2F", bg: "#FBF4E9", border: "#E8D3A8" },
  }[tone] || { color: "#575A57", bg: "#FAFAF7", border: "#E5E5E0" };

  const display = value == null ? "—" : value;

  return (
    <div
      className="rounded-md p-2 text-center border"
      style={{ background: styles.bg, borderColor: styles.border }}
    >
      <div className="text-[10px] uppercase tracking-wider" style={{ color: styles.color }}>
        {label}
      </div>
      <div className="text-2xl font-mono font-semibold mt-0.5" style={{ color: styles.color }}>
        {display}
      </div>
      {error && tone === "nayax" && (
        <div className="text-[9px] text-[#C05C47] mt-0.5 italic">indisponible</div>
      )}
    </div>
  );
}
